import React, { useState } from 'react';
import { Phone, MessageCircle, Instagram, Linkedin, Facebook, MapPin, Menu, X } from 'lucide-react';
import { 
  BUSINESS_PHONE, 
  BUSINESS_NAME, 
  INSTAGRAM_URL, 
  LINKEDIN_URL, 
  FACEBOOK_URL, 
  GOOGLE_REVIEWS_URL 
} from '../constants.tsx';
import { Language, TRANSLATIONS } from '../translations.ts';
import NavigationLink from './NavigationLink.tsx';

interface HeaderProps {
  onOpenBooking: () => void;
  onNavigateAbout: () => void;
  onNavigateServices: () => void;
  onNavigateLocations: () => void;
  onNavigatePricing: () => void;
  onNavigateHome: () => void;
  isHome: boolean;
  currentView: string;
  lang: Language;
  onLangChange: (lang: Language) => void;
}

const Header: React.FC<HeaderProps> = ({ 
  onOpenBooking, 
  onNavigateAbout, 
  onNavigateServices,
  onNavigateLocations,
  onNavigatePricing,
  onNavigateHome, 
  isHome,
  currentView,
  lang,
  onLangChange
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const whatsappUrl = `https://wa.me/61${BUSINESS_PHONE.replace(/\s/g, '').slice(1)}`;
  const t = TRANSLATIONS[lang];

  const languages: { code: Language; label: string }[] = [
    { code: 'en', label: 'EN' },
    { code: 'zh', label: '中文' },
    { code: 'ko', label: '한국어' },
    { code: 'es', label: 'ES' }
  ];

  const handleMobileNav = (action: () => void) => {
    setIsMobileMenuOpen(false);
    action();
  };

  const navigateToRepairs = () => {
    window.history.pushState({}, '', '/repairs');
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo(0, 0);
  };

  return (
    <div className="sticky top-0 z-[100]">
      {/* Top Bar - High Visibility */}
      <div className="bg-navy text-white py-2 px-4 border-b border-white/10 shadow-sm relative z-[101]">
        <div className="max-w-[90rem] mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 md:gap-4 text-[9px] md:text-xs font-bold tracking-wider">
            {/* Language Switcher */}
            <div className="flex items-center bg-white/5 rounded-full px-1 py-0.5 border border-white/10">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => onLangChange(l.code)}
                  aria-label={`Switch language to ${l.label}`}
                  className={`px-2 py-1 rounded-full transition-all text-[8px] md:text-[10px] ${
                    lang === l.code ? 'bg-green-600 text-white' : 'text-white/50 hover:text-white'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <span className="hidden lg:inline opacity-30">|</span>
            <div className="hidden sm:flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-black">Active Now</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4 md:gap-8">
            {/* Social Group */}
            <div className="hidden md:flex items-center gap-5 border-r border-white/20 pr-8">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Visit Facebook Page" className="text-white hover:text-blue-400 hover:scale-125 transition-all"><Facebook size={16} /></a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Visit Instagram Page" className="text-white hover:text-pink-400 hover:scale-125 transition-all"><Instagram size={16} /></a>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Visit LinkedIn Profile" className="text-white hover:text-blue-500 hover:scale-125 transition-all"><Linkedin size={16} /></a>
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" aria-label="View Google Reviews" className="text-white hover:text-yellow-400 hover:scale-125 transition-all"><MapPin size={16} /></a>
            </div>

            {/* Top Bar Contact Links */}
            <div className="flex items-center gap-3 md:gap-6">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`${t.whatsapp} us directly via WhatsApp`}
                aria-label={`Message ${BUSINESS_NAME} on WhatsApp`}
                className="flex items-center gap-1.5 text-[#25D366] font-black uppercase text-[10px] md:text-xs hover:text-white transition-all border-r border-white/10 pr-3 md:pr-6"
              >
                <MessageCircle size={14} fill="currentColor" />
                <span>{t.whatsapp}</span>
              </a>
              <a 
                href={`tel:${BUSINESS_PHONE.replace(/\s/g, '')}`}
                title={`Call us at ${BUSINESS_PHONE}`}
                aria-label={`Call ${BUSINESS_NAME} for emergency tech support`}
                className="flex items-center gap-1.5 text-green-400 font-black uppercase text-[10px] md:text-xs hover:text-green-300 transition-all"
              >
                <Phone size={14} fill="currentColor" />
                <span>{t.call_ike}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <header className="bg-white/95 backdrop-blur-md shadow-lg border-b border-slate-200 relative z-[100]">
        <div className="max-w-[90rem] mx-auto px-4 lg:px-8 h-20 md:h-24 flex items-center justify-between">
          <NavigationLink 
            href="/"
            onClick={onNavigateHome} 
            aria-label={`${BUSINESS_NAME} Homepage`}
            title={`Return to ${BUSINESS_NAME} Homepage`}
            className="flex items-center gap-3 group transition-all shrink-0"
          >
            {/* Custom Logo: Monitor with Bandage & Thumbs Up */}
            <div className="w-12 h-12 md:w-16 md:h-16 relative group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 ease-out">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-md">
                {/* Monitor Stand */}
                <path d="M100 165V185" stroke="#1A365D" strokeWidth="16" strokeLinecap="round"/>
                <path d="M70 185H130" stroke="#1A365D" strokeWidth="16" strokeLinecap="round"/>
                
                {/* Monitor Frame */}
                <rect x="20" y="20" width="160" height="130" rx="20" fill="#1A365D" />
                
                {/* Screen (Green for 'Fixed/Good') */}
                <rect x="35" y="35" width="130" height="100" rx="10" fill="#2F855A" />
                
                {/* Screen Glare/Reflection */}
                <path d="M35 35 L95 35 L60 135 L35 135 Z" fill="white" fillOpacity="0.07" />

                {/* Thumbs Up Icon (Simplified White) */}
                <path d="M90 105H75V70H90V105ZM90 70L105 45C105 45 110 40 115 45C120 50 115 60 115 60V70H140C145 70 150 75 150 80V95C150 100 145 105 140 105H90V70Z" fill="white"/>

                {/* The "Loved One" Bandage - Top Right Corner */}
                <g transform="translate(155, 25) rotate(30)">
                    <rect x="-25" y="-10" width="50" height="20" rx="6" fill="#ECC94B" stroke="#1A365D" strokeWidth="4" />
                    {/* Bandage Dots */}
                    <circle cx="-12" cy="0" r="2.5" fill="#1A365D" fillOpacity="0.4" />
                    <circle cx="0" cy="0" r="2.5" fill="#1A365D" fillOpacity="0.4" />
                    <circle cx="12" cy="0" r="2.5" fill="#1A365D" fillOpacity="0.4" />
                </g>
              </svg>
            </div>
            
            <span className="font-black text-navy text-lg md:text-2xl tracking-tighter uppercase leading-none">
              {BUSINESS_NAME.split(' ').map((word, i) => (
                <span key={i} className={i === 1 ? 'text-green-700' : ''}>{word} </span>
              ))}
            </span>
          </NavigationLink>

          {/* Desktop Navigation - 18px Semi-bold */}
          <nav aria-label="Main Navigation" role="navigation" className="hidden lg:flex items-center gap-6 xl:gap-8 text-navy font-semibold text-base xl:text-[18px]">
            <NavigationLink href="/about" onClick={onNavigateAbout} aria-label="About Us" className={`hover:text-green-700 transition-all relative py-2 ${currentView === 'about' ? 'text-green-700 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-700' : ''}`}>{t.nav_about}</NavigationLink>
            <NavigationLink href="/services" onClick={onNavigateServices} aria-label="Our Services" className={`hover:text-green-700 transition-all relative py-2 ${currentView === 'services' ? 'text-green-700 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-700' : ''}`}>{t.nav_services}</NavigationLink>
            <NavigationLink href="/repairs" onClick={navigateToRepairs} aria-label="Recent Repairs" className={`hover:text-green-700 transition-all relative py-2 ${currentView === 'repairs' ? 'text-green-700 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-700' : ''}`}>Repairs</NavigationLink>
            <NavigationLink href="/locations" onClick={onNavigateLocations} aria-label="Service Locations" className={`hover:text-green-700 transition-all relative py-2 ${currentView === 'locations' ? 'text-green-700 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-700' : ''}`}>{t.nav_locations}</NavigationLink>
            <NavigationLink href="/pricing" onClick={onNavigatePricing} aria-label="Pricing" className={`hover:text-green-700 transition-all relative py-2 ${currentView === 'pricing' ? 'text-green-700 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-green-700' : ''}`}>{t.nav_pricing}</NavigationLink>
          </nav>

          <div className="flex items-center gap-2 md:gap-4 shrink-0">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp" className="lg:hidden w-11 h-11 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-transform"><MessageCircle size={24} fill="currentColor" /></a>
            <a 
              href={`tel:${BUSINESS_PHONE.replace(/\s/g, '')}`} 
              aria-label={`Call ${BUSINESS_NAME} for emergency tech support`}
              title={`Call us at ${BUSINESS_PHONE}`}
              className="hidden sm:flex items-center gap-2 text-navy font-black hover:text-green-700 transition-colors text-lg"
            >
              <Phone size={20} fill="currentColor" />
              <span className="hidden lg:inline">{BUSINESS_PHONE}</span>
              <span className="lg:hidden">Call</span>
            </a>
            
            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-navigation"
              className="lg:hidden p-2 text-navy hover:text-green-700 transition-colors"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            <button onClick={onOpenBooking} className="hidden md:block bg-green-700 text-white px-5 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-black shadow-xl hover:bg-green-800 transition-all text-xs md:text-sm uppercase tracking-widest">Get Quote</button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        <div 
          id="mobile-navigation"
          className={`lg:hidden absolute top-full left-0 w-full bg-white border-b border-slate-100 shadow-2xl transition-all duration-300 ease-in-out overflow-hidden ${isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <nav className="flex flex-col p-6 space-y-4">
            <NavigationLink 
              href="/about"
              onClick={() => handleMobileNav(onNavigateAbout)} 
              className="text-xl font-bold text-navy hover:text-green-700 text-left py-2 border-b border-slate-50"
            >
              {t.nav_about}
            </NavigationLink>
            <NavigationLink 
              href="/services"
              onClick={() => handleMobileNav(onNavigateServices)} 
              className="text-xl font-bold text-navy hover:text-green-700 text-left py-2 border-b border-slate-50"
            >
              {t.nav_services}
            </NavigationLink>
            <NavigationLink 
              href="/repairs"
              onClick={() => handleMobileNav(navigateToRepairs)} 
              className="text-xl font-bold text-navy hover:text-green-700 text-left py-2 border-b border-slate-50"
            >
              Repairs
            </NavigationLink>
            <NavigationLink 
              href="/locations"
              onClick={() => handleMobileNav(onNavigateLocations)} 
              className="text-xl font-bold text-navy hover:text-green-700 text-left py-2 border-b border-slate-50"
            >
              {t.nav_locations}
            </NavigationLink>
            <NavigationLink 
              href="/pricing"
              onClick={() => handleMobileNav(onNavigatePricing)} 
              className="text-xl font-bold text-navy hover:text-green-700 text-left py-2 border-b border-slate-50"
            >
              {t.nav_pricing}
            </NavigationLink>
            <button 
              onClick={() => handleMobileNav(onOpenBooking)} 
              className="mt-4 w-full bg-green-700 text-white py-4 rounded-xl font-black text-lg shadow-lg active:scale-95"
            >
              Get a Quote
            </button>
          </nav>
        </div>
      </header>
    </div>
  );
};

export default Header;